/**
 * KsaPage - the GLOBAL KSA page body: KSA Opportunities and KSA Projects on
 * one screen, the real OpportunitiesView above the real ProjectsView (both
 * running on KSA records via KsaDataProvider). Marking an opportunity Won
 * moves it down into the Projects table, as it moves tabs on the main side.
 *
 * Opening a project swaps the Projects view for its full-page detail; the
 * Opportunities above are hidden meanwhile (not unmounted, so their filters
 * and sort survive). The page scrolls to the top for the detail, and "Back to
 * Projects" brings the Projects table back into view, where you left from.
 *
 * Above both, one line of company totals for the Won KSA projects (in SAR,
 * with USD beside it) and the "Exchange rates" button (r44, Shawn 9/30/26).
 * Beside it, for Quotes Edit only, "Quote rates" (10/7/26): GLOBAL KSA's own
 * quote rate card in Saudi Riyals - the Engineers tab's QuoteRatesPanel,
 * here on the KSA card because the panel takes the tab's API base.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { hasArea } from '../../data/roles';
import { useMyAccess } from '../../hooks/useMyAccess';
import { Modal } from '../ui/Modal';
import QuoteRatesPanel from '../engineers/QuoteRatesPanel';
import { useApiBase } from '../../context/DataScope';
import OpportunitiesView from '../../views/OpportunitiesView';
import ProjectsView from '../../views/ProjectsView';
import KsaRatesPanel from './KsaRatesPanel';
import { KSA_COSTS_CHANGED, ksaCall } from './KsaCostsPanel';
import { addMoney, fmtMoney, fmtPct, fmtRate, num } from './ksaMoney';

/**
 * "Won KSA projects: revenue SAR X · costs SAR Y · profit SAR Z (margin M%)
 * · ≈ USD W at 3.75 SAR per USD", from GET {base}/summary (the server adds
 * up the Won / Complete KSA projects and converts each to SAR and USD).
 * `version` changes whenever the KSA records reload, so an edit shows here.
 */
export function KsaTotals({ version, onRates, onQuoteRates }) {
  const base = useApiBase();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      setData(await ksaCall(`${base}/summary`, undefined, 'Could not load the KSA totals'));
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [base]);
  useEffect(() => {
    load();
  }, [load, version]);
  useEffect(() => {
    window.addEventListener(KSA_COSTS_CHANGED, load);
    return () => window.removeEventListener(KSA_COSTS_CHANGED, load);
  }, [load]);

  const w = (data && data.won) || null;
  const count = w ? num(w.count) || 0 : 0;
  const usdPerSar = w && num(w.usd_per_sar);
  const profit = w && num(w.profit_sar);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '10px 14px',
        background: '#F3F7F1',
        border: '1px solid #DCE7D6',
        borderRadius: 10,
      }}
    >
      <div
        style={{ flex: 1, minWidth: 0, fontSize: 12, color: '#333', lineHeight: 1.55, fontVariantNumeric: 'tabular-nums' }}
        aria-label="Won KSA totals"
      >
        {error ? (
          <span role="alert" style={{ color: '#B71C1C', fontWeight: 600 }}>
            {error}
          </span>
        ) : !data ? (
          <span style={{ color: '#999' }}>Loading the KSA totals...</span>
        ) : count === 0 ? (
          <span>
            <b style={{ color: '#1B5E20' }}>Won KSA projects:</b>{' '}
            {w && Array.isArray(w.left_out) && w.left_out.length && data.currency_note ? (
              <span style={{ color: '#92400E' }}>{data.currency_note}</span>
            ) : (
              'none yet.'
            )}
          </span>
        ) : (
          <>
            <div>
              <b style={{ color: '#1B5E20' }}>Won KSA projects ({count}):</b> revenue <b>{fmtMoney(w.revenue_sar, 'SAR')}</b>
              {' · '}costs <b>{fmtMoney(w.costs_sar ?? addMoney(w.labor_sar, w.purchases_sar), 'SAR')}</b>
              <span style={{ color: '#777' }}>
                {' '}
                (labor {fmtMoney(w.labor_sar, 'SAR')}, purchases {fmtMoney(w.purchases_sar, 'SAR')})
              </span>
              {' · '}profit so far{' '}
              <b style={{ color: profit != null && profit < 0 ? '#B71C1C' : '#1B5E20' }}>{fmtMoney(w.profit_sar, 'SAR')}</b> (margin{' '}
              {fmtPct(w.margin)})
            </div>
            <div style={{ color: '#555', fontSize: 11.5 }}>
              ≈ USD: revenue {fmtMoney(w.revenue_usd, 'USD')} · profit {fmtMoney(w.profit_usd, 'USD')}
              {usdPerSar ? `, at ${fmtRate(1 / usdPerSar)} SAR per USD` : ''}
              {data.currency_note ? <span style={{ color: '#888' }}> · {data.currency_note}</span> : null}
            </div>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={onRates}
        style={{
          flexShrink: 0,
          background: '#fff',
          color: '#1B5E20',
          border: '1px solid #C8E6C9',
          padding: '8px 14px',
          borderRadius: 6,
          fontSize: 11,
          fontWeight: 700,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        Exchange rates
      </button>
      {onQuoteRates && (
        <button
          type="button"
          onClick={onQuoteRates}
          title="The KSA quote rate card, in Saudi Riyals"
          style={{
            flexShrink: 0,
            background: '#fff',
            color: '#1B5E20',
            border: '1px solid #C8E6C9',
            padding: '8px 14px',
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Quote rates
        </button>
      )}
    </div>
  );
}

export default function KsaPage() {
  const { ksaError, allProjects } = useApp();
  const [ratesOpen, setRatesOpen] = useState(false);
  // the KSA quote rate card: Quotes Edit only (the server says the same)
  const seesQuoteRates = hasArea(useMyAccess(), 'quotes', 'edit');
  const [quoteRatesOpen, setQuoteRatesOpen] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const top = useRef(null);
  const projects = useRef(null);
  const wasOpen = useRef(false);

  // after the re-render, so the Opportunities are already hidden / shown again
  useEffect(() => {
    if (projectOpen) {
      // the app's scrolling pane, not the page root (that would hide its padding)
      const pane = top.current && top.current.closest('.app-content-padding');
      if (pane) pane.scrollTo({ top: 0 });
    } else if (wasOpen.current && projects.current) {
      projects.current.scrollIntoView({ block: 'start' });
    }
    wasOpen.current = projectOpen;
  }, [projectOpen]);

  return (
    <div ref={top} className="space-y-4">
      {ksaError && (
        <div
          style={{
            padding: '8px 14px',
            background: '#FFEBEE',
            border: '1px solid #FFCDD2',
            borderRadius: 8,
            color: '#B71C1C',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {ksaError}
        </div>
      )}
      <KsaTotals
        version={allProjects}
        onRates={() => setRatesOpen(true)}
        onQuoteRates={seesQuoteRates ? () => setQuoteRatesOpen(true) : null}
      />
      {ratesOpen && <KsaRatesPanel onClose={() => setRatesOpen(false)} />}
      {quoteRatesOpen && seesQuoteRates && (
        <Modal title="GLOBAL KSA quote rates" onClose={() => setQuoteRatesOpen(false)} width={1200}>
          <QuoteRatesPanel editable />
        </Modal>
      )}
      <section aria-label="KSA Opportunities" style={projectOpen ? { display: 'none' } : undefined}>
        <OpportunitiesView />
      </section>
      {!projectOpen && <hr style={{ border: 0, borderTop: '1px solid #D8DED4', margin: '28px 0 20px' }} />}
      <section ref={projects} aria-label="KSA Projects">
        <ProjectsView onDetailChange={setProjectOpen} />
      </section>
    </div>
  );
}
